import React, { useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppContext } from 'services/context';
import { NOTIFICATION_TYPES, ROLES } from 'constants/index.js';
import { useScreenSize, useToggle, useTranslation } from 'services/hooks';
import { prepereConversationFileToUpload } from 'services/utils';
import { COURSE_TYPES, CONVERSATION_STATUS, MESSAGE_STATUS, USER_APPLICATION_STATUS } from 'constants/index.js';
import { USER_ACTIONS } from 'services/reducers/actionTypes';
import Container from 'components/Container';
import Button from 'components/Button';
import Input from 'components/Input';
import Select from 'components/Input/Select';
import DatePicker from 'components/Input/DatePicker';
import Table from 'pages/ManagementPanel/Table';
import Icon from 'components/Icon';
import ConversationCard from './ConversationCard';
import Message, { ParentMessage } from './Message';
import File from './File';
import Modal from 'components/Modal';
import TextArea from 'components/Input/TextArea';
import AutoCompleteSelect from 'components/Input/AutoCompleteSelect';

import './style.css';

const _searchFormData = {
	// email: '',
	// fName: '',
	// lName: '',
	// subject: '',
	// statusId: 0,
	// classId: 0,
	// courseId: 0,
	// dateFrom: '',
	// dateTo: '',
};

const TRANSLATION_KEY = 'conversation';
const VALIDATIONS_TRANSLATION_KEY = 'validations';

const Conversation = () => {
	const { user } = useAppContext();

	const [conversationId, setConversationId] = useState();

	if (user.roleId !== ROLES.Student) {
		return (
			<>
				{!conversationId && <ConversationManagement setConversationId={setConversationId} />}
				{conversationId && <ConversationChat conversationId={conversationId || null} setConversationId={setConversationId} />}
			</>
		);
	}
	return <ConversationChat conversationId={conversationId || null} setConversationId={setConversationId} />;
};
export default Conversation;

export const ConversationManagement = ({ setConversationId }) => {
	const { user, ddls, Api } = useAppContext();

	const { courses, classes, conversationStatus, courseTeachers } = ddls;

	const [searchFormData, setSearchFormData] = useState(_searchFormData);
	const [searchFormResult, setSearchFormResult] = useState([]);
	const [currentConversation, setCurrentConversation] = useState({});
	const [selectedUser, setSelectedUser] = useState({});

	//AutoCompleteSelect related;
	const [wordEntered, setWordEntered] = useState('');
	const [conversationTarget, setConversationTarget] = useState('student');
	const [message, setMessage] = useState('');
	const [usersList, setUsersList] = useState([]);

	//toggles for Modals
	const [addConversationModalVisible, addConversationModalVisibleToggle] = useToggle(false);
	const [editConversationModalVisible, editConversationModalVisibleToggle] = useToggle(false);

	const searchFormRef = useRef();
	const newConversationFormRef = useRef();
	const innerFormRef = useRef(null);
	const getTranslation = useTranslation();

	const isTeacher = useMemo(() => {
		return user.roleId === ROLES.Teacher || user.roleId === ROLES.TeacherTeamLeader;
	}, [user]);

	const isAdmin = useMemo(() => {
		return user.roleId === ROLES.Admin;
	}, [user]);

	const coursesOptionsByTeacher = useMemo(() => {
		const allReleventCourses = courses?.options.filter(
			course => course.courseTypeId === COURSE_TYPES.IELTS || course.courseTypeId === COURSE_TYPES.ESL
		);
		const courseOfTeacher = courseTeachers?.options?.filter(teacher => {
			return teacher.value === user.userId;
		});
		if (!courseOfTeacher.length) return allReleventCourses;

		const matchedCourses = courses?.options?.filter(course => courseOfTeacher?.some(obj => obj.courseId === course.value));
		return matchedCourses;
	}, [courses, courseTeachers, user]);

	const isIELTSTeacher = useMemo(() => {
		return coursesOptionsByTeacher.some(course => course.courseTypeId === COURSE_TYPES.IELTS) || isAdmin;
	}, [coursesOptionsByTeacher, isAdmin]);

	const conversationStatusOptions = useMemo(() => {
		return conversationStatus.options.map(({ value, description }) => {
			return { value, description };
		});
	}, [conversationStatus]);

	const classOfTeacher = useMemo(() => {
		if (isAdmin) return classes.options;
		const _classOfTeacher = classes?.options?.filter(c => c.classTeacherUserId === user.userId);
		if (!_classOfTeacher) return [];
		return _classOfTeacher;
	}, [classes, isAdmin, user]);

	const isStudentFrozen = useMemo(() => {
		//Teacher cannot send messages to user in frozen status.
		return isTeacher && selectedUser && selectedUser?.userApplicationStatusId === USER_APPLICATION_STATUS.FROZEN;
	}, [selectedUser, isTeacher]);

	//TABLES COLUMNS AND HEADERS
	const searchFormDataColumns = useMemo(() => {
		return [
			{
				Header: ' ',
				columns: [
					{
						//running number - rows counter
						Header: '#',
						// accessor: 'userId',
						Cell: row => <>{row.row.index + 1}</>,
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_date_column`),
						accessor: 'dateCreated',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_conversation_status_column`),
						accessor: 'statusName',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_sender_column`),
						Cell: row => {
							const { original } = row?.cell?.row || {};
							return `${original.fromUserName}-${original.fromUserEmail}`;
						},
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_subject_column`),
						Cell: row => {
							const { original } = row?.cell?.row || {};
							return original.subject;
						},
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_recipient_column`),
						accessor: 'toUserName',
					},

					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_course_status_column`),
						accessor: 'courseStatusName',
					},
					//shows class column only for ielts teacher
					...(isIELTSTeacher
						? [
								{
									Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_class_column`),
									accessor: 'className',
								},
						  ]
						: []),

					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_class_status_column`),
						accessor: 'classStatusName',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_unread_messages_column`),
						Cell: row => {
							const { original } = row?.cell?.row || {};
							return <span className="notification">{original.unreadMessagesCount || ''}</span>;
						},
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_edit_details_column`),
						Cell: row => (
							<div
								className="flex"
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_edit_details_column_title`)}
								onClick={() => handleEditConversationClick(row)}
							>
								<Icon icon="editIcon" />
							</div>
						),
					},
					//shows Answer Conversation column only for teacher (not for admin)
					...(isTeacher
						? [
								{
									Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_answer_column`),
									Cell: row => (
										<div
											className="flex"
											title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_answer_column`)}
											onClick={() => handleAnswerConversationClick(row)}
										>
											<Icon icon="editIcon" />
										</div>
									),
								},
						  ]
						: []),
				],
			},
		];
	}, [isIELTSTeacher, searchFormResult, isTeacher, getTranslation]);

	//search bar onChange value handler
	const _handleOnChange = value => {
		setWordEntered(value);
	};

	const _handleClickAddNewConversation = () => {
		setMessage('');
		addConversationModalVisibleToggle();
	};

	// ////CELL CLICK CALLBACKS
	const handleEditConversationClick = row => {
		const { original } = row?.cell?.row || {};
		if (!original) return;
		setCurrentConversation(original);
		editConversationModalVisibleToggle();
	};

	//Answer conversation button on Table
	const handleAnswerConversationClick = row => {
		const { original } = row?.cell?.row || {};
		if (!original) return;
		if (setConversationId && typeof setConversationId === 'function') setConversationId(original.conversationId);
	};

	//search bar onChange value handler
	const _handleGetOnSelectedUserId = e => {
		const user = usersList.find(user => {
			return user.description === e.target.value;
		});
		setSelectedUser(user);
	};

	const _handleOnPaste = e => {
		async function fetchData() {
			try {
				const PastedValue = e.clipboardData.getData('Text').replace('[ \t]+$').trim();
				const _userEmail = await Api.GetConversationUsersListLike(user.userId, PastedValue);
				if (!_userEmail || !_userEmail.length > 1) return;
				const email = _userEmail.map(({ userName, userId, userApplicationStatusId }) => ({
					id: userId,
					description: userName,
					userApplicationStatusId,
				}));
				setUsersList(email);
			} catch (ex) {
				console.warn(ex);
			}
		}
		fetchData();
	};

	//GetConversationUsersListLike for user selection
	useEffect(() => {
		//fetch only when has exactly 3 charcters
		if (wordEntered.length > 1) return;
		if (wordEntered.length < 1) {
			setUsersList([]);
			return;
		}
		async function fetchData() {
			try {
				const _usersList = await Api.GetConversationUsersListLike(user.userId, wordEntered);
				if (!_usersList || !_usersList.length > 1) return;
				setUsersList(
					_usersList.map(({ userName, userId, userApplicationStatusId }) => ({
						id: userId,
						description: userName,
						userApplicationStatusId,
					}))
				);
			} catch (exception) {}
		}
		fetchData();
	}, [wordEntered, Api]);

	//handles form clear button
	const _clearSearchForm = e => {
		e.preventDefault();
		setSearchFormData(_searchFormData);
		searchFormRef?.current?.reset();
		setSearchFormResult([]);
	};

	//handles changing inputs
	const _handleChangeInput = (value, key) => {
		setMessage('');
		const _formData = { ...searchFormData };
		_formData[key] = value;
		setSearchFormData(_formData);
	};

	//handle search conversations form
	const _handleSearchConversations = async e => {
		try {
			e.preventDefault();
			const form = new FormData(e.target);
			let formData = Object.fromEntries(form.entries());

			const res = await Api.SearchConversations(formData);
			if (res && res.length > 0) {
				setSearchFormResult(res);
			} else {
				setMessage(getTranslation(TRANSLATION_KEY, res.message));
				setSearchFormResult([]);
			}
		} catch (error) {
			console.warn(error);
		}
	};

	//handle insert and update conversation(add/edit);
	const _handleAddNewConversation = async e => {
		e.preventDefault();
		const form = new FormData(e.target);
		const formData = Object.fromEntries(form.entries());
		const innerFormValues = innerFormRef.current.getValues();
		if (!formData.subject || !innerFormValues.message) return;
		const _userId = selectedUser?.id;
		formData.toUserId = _userId;
		formData.messageBody = innerFormValues.message;
		if (innerFormValues.file) {
			formData.fileSFix = innerFormValues.file.fileSFix;
			formData.fileName = innerFormValues.file.fileName;
			formData.fileData = innerFormValues.file.fileData;
		}
		const res = await Api.InsertUpdateConversation(formData);
		if (!res) return;
		setCurrentConversation(res);
		if (setConversationId && typeof setConversationId === 'function') setConversationId(res.conversationId);
		addConversationModalVisibleToggle();
	};

	//handles update subject and conversation status
	const handleSubmitConversationStatus = async e => {
		e.preventDefault();
		const form = new FormData(e.target);
		const formData = Object.fromEntries(form.entries());
		const parametersToSend = {};
		//status changes
		parametersToSend.toStatusId = formData.statusId;
		parametersToSend.fromStatusId = currentConversation.statusId;
		//subject changes
		parametersToSend.subject = formData.subject;
		parametersToSend.conversationId = currentConversation.conversationId;
		const res = await Api.InsertUpdateConversation(parametersToSend);
		if (!res) return;
		//gets as searchResult the one that we changed.
		setSearchFormResult([res]);
		editConversationModalVisibleToggle();
	};

	//handle change student class radio buttons on IELTS Teacher
	const handleConversationTargetOnChange = e => {
		setSelectedUser({});
		setConversationTarget(e.target.value);
	};

	return (
		<div className="ConversationManagement">
			<Container>
				<form ref={searchFormRef} onSubmit={_handleSearchConversations}>
					<div className="conversation__wrapper">
						<div className="title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_title`)}</div>
						<div className="inputs_grid__starter">
							<Input
								name="email"
								placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_email_field`)}
								onChange={({ target }) => _handleChangeInput(target.value?.trim() || '', 'email')}
							/>
							<Input
								name="fName"
								placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_first_name_field`)}
								onChange={({ target }) => _handleChangeInput(target.value?.trim() || '', 'fName')}
							/>
							<Input
								name="lName"
								placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_last_name_field`)}
								onChange={({ target }) => _handleChangeInput(target.value?.trim() || '', 'lName')}
							/>
						</div>
						<div className="title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_properties_title`)}</div>
						<div className="inputs_grid__properties">
							<Input
								defaultValue={''}
								name="subject"
								placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_properties_subject_field`)}
								onChange={({ target }) => _handleChangeInput(target.value?.trim() || '', 'subject')}
							/>
							<Select
								placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_properties_status_field`)}
								name="statusId"
								onChange={({ target }) => _handleChangeInput(target.value || null, 'statusId' || '')}
								options={conversationStatusOptions}
							/>
							<Select
								placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_properties_course_field`)}
								name="courseId"
								onChange={({ target }) => _handleChangeInput(target.value || null, 'courseId' || '')}
								options={coursesOptionsByTeacher}
							/>
							{isIELTSTeacher && (
								<Select
									placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_properties_class_field`)}
									name="classId"
									onChange={({ target }) => _handleChangeInput(target.value || null, 'classId' || '')}
									options={classOfTeacher}
								/>
							)}
							<DatePicker
								fromTo
								startDateName="dateFrom"
								endDateName="dateTo"
								onFromChange={value => _handleChangeInput(value, 'dateFrom')}
								onToChange={value => _handleChangeInput(value, 'dateTo')}
							/>
						</div>
						<div className="buttons_wrapper">
							<Button small title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_properties_search_button`)} />
							<Button
								small
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_properties_clear_button`)}
								onClick={_clearSearchForm}
							/>
						</div>
					</div>
				</form>
				{(!isAdmin || searchFormResult.length > 0 || message) && (
					<div className="ConversationManagement__results">
						<div className="ConversationManagement__results__message">
							{searchFormResult.length > 0
								? `${searchFormResult.length} ${getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_count_results_note`)}`
								: ''}
							{message && message}
						</div>
						{/* An admin cannot open a new conversation */}
						{!isAdmin && (
							<Button
								small
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_add_button`)}
								onClick={_handleClickAddNewConversation}
							/>
						)}
					</div>
				)}
				{searchFormResult && searchFormResult.length > 0 && (
					<div className="ConversationManagement__results__table">
						<Table data={searchFormResult} columns={searchFormDataColumns} pageSizeProp={10} />
					</div>
				)}
			</Container>
			<Modal
				extraClassName="addNewConversationModal"
				isVisible={addConversationModalVisible}
				setVisibility={addConversationModalVisibleToggle}
			>
				<form ref={newConversationFormRef} onSubmit={_handleAddNewConversation}>
					<div className="conversation__wrapper add_conversation">
						<div className="title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_starter_title`)}</div>
						<div className="starter">{user && `${user.fname} ${user.lname}`}</div>
						<div className="title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_recipient_title`)}</div>
						<div className="inputs_grid__starter">
							{isIELTSTeacher && (
								<div className="inputs_grid__starter__radios">
									<label>
										<input
											type="radio"
											value={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_student_field`)?.toLowerCase()}
											defaultChecked={conversationTarget === 'student' || ''}
											name="conversationTarget"
											onChange={handleConversationTargetOnChange}
										/>
										{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_student_field`)}
									</label>
									<label>
										<input
											type="radio"
											value={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_class_field`)?.toLowerCase()}
											defaultChecked={conversationTarget === 'class' || ''}
											name="conversationTarget"
											onChange={handleConversationTargetOnChange}
										/>
										{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_class_field`)}
									</label>
								</div>
							)}
							{conversationTarget === 'student' && (
								<AutoCompleteSelect
									label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_email_field`)}
									onChange={_handleOnChange}
									data={usersList}
									onBlur={_handleGetOnSelectedUserId}
									onPaste={_handleOnPaste}
									withoutSelectButton
								/>
							)}
							{conversationTarget === 'class' && (
								<Select
									placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_class_field`)}
									name="classId"
									options={classOfTeacher}
									onChange={({ target }) => _handleChangeInput(target.value || null, 'classId' || '')}
								/>
							)}
						</div>
						{isTeacher && addConversationModalVisible && isStudentFrozen && (
							<div className="frozen_user_error_message">
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_frozen_user_error_message`)}
							</div>
						)}
						<div className="title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_subject_title`)}</div>
						<div className="inputs_grid__properties">
							<Input
								defaultValue={''}
								name="subject"
								placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_subject_field`)}
								onChange={({ target }) => _handleChangeInput(target.value?.trim() || '', 'subject')}
							/>
						</div>
						<ConversationInput hideSendBtn ref={innerFormRef} preventEnterPress isCountOfCharsVisible />
						<Button small title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_start_button`)} disabled={isStudentFrozen} />
					</div>
				</form>
			</Modal>
			<Modal
				extraClassName="editConversationModal"
				isVisible={editConversationModalVisible}
				setVisibility={editConversationModalVisibleToggle}
			>
				<form ref={newConversationFormRef} onSubmit={handleSubmitConversationStatus}>
					<div className="conversation__wrapper edit_conversation">
						{/* Edit Conversation Status */}
						<div className="title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_edit_modal_status_title`)}</div>
						<div className="inputs_grid__properties">
							<Select
								placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_edit_modal_status_field`)}
								name="statusId"
								onChange={({ target }) => _handleChangeInput(target.value || null, 'statusId' || '')}
								options={conversationStatus?.options}
								defaultValue={currentConversation.statusId || ''}
							/>
						</div>

						{/* Edit Conversation Subject */}
						<div className="title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_edit_modal_subject_title`)}</div>
						<Input
							name="subject"
							placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_edit_modal_subject_field`)}
							defaultValue={currentConversation.subject || ''}
							onChange={({ target }) => _handleChangeInput(target.value?.trim() || '', 'subject')}
						/>

						<div className="buttons_wrapper">
							<Button small title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_edit_modal_save_button`)} />
						</div>
					</div>
				</form>
			</Modal>
		</div>
	);
};

export const ConversationChat = ({ conversationId, setConversationId }) => {
	const { Api, user, courses, dispatchUser, addNotification } = useAppContext();
	const [userConversations, setUserConverastions] = useState([]);
	const [currentConversation, setCurrentConversation] = useState({});
	const [sessionMessages, setSessionMessages] = useState([]); //for all the saved messages
	const [AddConversationUserModalVisible, AddConversationUserModalVisibleToggle] = useToggle(false);
	const [filterByEmail, setFilterByEmail] = useState('');
	const [replayMessageData, setReplayMessageData] = useState(undefined);
	const [redirectedFromMyCourse, setRedirectedFromMyCourse] = useState(false);
	const [markedMessageId, setMarkedMessageId] = useState(false);
	const location = useLocation();
	const { state } = location;
	const innerFormRef = useRef(null);
	const conversationRef = useRef(null);
	const sessionMessagesContainerRef = useRef(null);

	const MOBILE_SIZE = process.env.REACT_APP_MOBILE_SIZE;
	const screenSize = useScreenSize();
	const getTranslation = useTranslation();

	// const messageId = useId();
	const addConversationFormRef = useRef();

	const toUserId = useMemo(() => {
		if (!currentConversation) return 0;
		const _toUserId = user.userId !== currentConversation.toUserId ? currentConversation.toUserId : currentConversation.fromUserId;
		return _toUserId;
	}, [currentConversation, user]);

	const isTeacher = useMemo(() => {
		return user.roleId === ROLES.Teacher || user.roleId === ROLES.TeacherTeamLeader;
	}, [user]);

	const toUserName = useMemo(() => {
		if (!currentConversation) return 0;
		const toUserName = user.userId !== currentConversation.fromUserId ? currentConversation.fromUserName : currentConversation.toUserName;
		return toUserName;
	}, [currentConversation, user]);

	const fromUserName = useMemo(() => {
		if (!currentConversation) return 0;
		const fromUserName = user.userId === currentConversation.fromUserId ? currentConversation.fromUserName : currentConversation.toUserName;
		return fromUserName;
	}, [currentConversation, user]);

	//Returns array of user conversations filtered by Email
	const filteredUserConversations = useMemo(() => {
		return userConversations?.filter(conversation =>
			filterByEmail ? conversation.fromUserEmail.includes(filterByEmail) || conversation.toUserEmail.includes(filterByEmail) : conversation
		);
	}, [userConversations, filterByEmail]);

	const currentConversationRecordIDNotInUserCourses = useMemo(() => {
		if (!currentConversation?.studentCourseRecordId || user.roleId !== ROLES.Student) return false; //New conversation.

		const _currentCourse = courses?.courses?.find(c => c.recordId === currentConversation?.studentCourseRecordId);
		if (!_currentCourse || _currentCourse.disabled) return true;

		return false;
	}, [currentConversation, courses]);

	const showConversationInput = useMemo(() => {
		return (
			Object.keys(currentConversation).length > 0 &&
			currentConversation.statusId !== CONVERSATION_STATUS.RESOLVED &&
			user.userApplicationStatusId !== USER_APPLICATION_STATUS.FROZEN &&
			!currentConversationRecordIDNotInUserCourses
		);
	}, [currentConversation, user, currentConversationRecordIDNotInUserCourses]);

	//related to MyCourse redirect user when click on Contact your teacher button
	useEffect(() => {
		if (state && state.contactTeacher) {
			// Run your specific event when contactTeacher is true
			AddConversationUserModalVisibleToggle();
			setRedirectedFromMyCourse(true);
		}
	}, [state]);
	//extract relevent teacher to student
	const teacherOptions = useMemo(() => {
		//extract relevant "Conversation Allowed" courses
		const releventCourses = courses?.courses?.filter(
			course => course.courseTypeId !== COURSE_TYPES.INTERNAL && course.studentTeacherId && !course.disabled
		);
		//filter user teachers if he have the same teacher in different courses
		const noneDuplicateTeachers = releventCourses?.reduce((acc, current) => {
			const isDuplicate = acc.some(item => item.studentTeacherId === current.studentTeacherId);
			if (!isDuplicate) {
				acc.push(current);
			}

			return acc;
		}, []);
		//if the user redirect here from MyCourse,it will return the actual relevent teacher
		if (redirectedFromMyCourse && state && state.studentTeacherId) {
			const actualTeacher = releventCourses?.find(_course => {
				return _course.studentTeacherId === state?.studentTeacherId && _course.recordId === state?.recordId;
			});
			return [{ value: actualTeacher.studentTeacherId, description: actualTeacher.studentTeacherName }];
		}

		//default option display object
		const teachersOfUser = noneDuplicateTeachers.map(_course => {
			return { value: _course.studentTeacherId, description: _course.studentTeacherName };
		});
		return teachersOfUser;
	}, [courses, state, redirectedFromMyCourse]);

	// const disableSendTooManyMessages = useMemo(() => {
	// 	if (!sessionMessages) return;
	// 	const todayMessages = sessionMessages.filter(
	// 		message => moment(message.dateCreated).isSame(moment(), 'day') && user.userId !== message.toUserId
	// 	);
	// 	return user.roleId === ROLES.Student && todayMessages.length > 10;
	// }, [sessionMessages]);

	//fetch all user conversations onload
	useEffect(() => {
		(async () => {
			const fetchConversations = await Api.GetAllConversationsByUserId();
			if (fetchConversations) setUserConverastions(fetchConversations);
		})();
	}, []);

	//when teacher choose conversation from ConversationManagement it will open the conversation that has been clicked
	useEffect(() => {
		if (Object.keys(currentConversation).length === 0 && conversationId) {
			_chooseConversation(conversationId);
		}
	}, [userConversations]);

	//handles send Message from ConversationInput
	const _handleSendMessage = async messageToSend => {
		if (!currentConversation) return;
		if (!messageToSend.message && !messageToSend.fileData) {
			console.error('Cannot send an empty message');
			return;
		}

		const message = {
			conversationId: currentConversation.conversationId,
			toUserId: toUserId,
			parentMessageId: messageToSend.parentMessageId || null,
			messageBody: messageToSend.message,
			fileSFix: messageToSend?.fileSFix || '',
			fileName: messageToSend?.fileName || '',
			fileData: messageToSend?.fileData || '',
		};

		const _message = await Api.InsertUpdateConversationMessage(message);

		if (!_message.messageId && !_message.isUpdate) {
			addNotification(getTranslation(TRANSLATION_KEY, _message.message), NOTIFICATION_TYPES.ERROR, 8000);

			return;
		}
		const updatedMessages = [_message, ...sessionMessages];
		setSessionMessages(updatedMessages);
		const _userConversation = [...userConversations];

		const updatedConversations = _userConversation.map(conversation =>
			conversation.conversationId === currentConversation?.conversationId
				? { ...conversation, messages: [...updatedMessages] }
				: conversation
		);

		setUserConverastions(updatedConversations);
		setReplayMessageData({});
		const _currentConversation = updatedConversations.find(c => c.conversationId === currentConversation.conversationId);
		setCurrentConversation({ ..._currentConversation });
		//scroll to new messages if needed
		sessionMessagesContainerRef.current.scrollTo({
			top: 100000,
			behavior: 'auto',
		});
	};

	//update the local state with the updated message
	const updateLocalMessage = updatedMessage => {
		//update the sessionMessages state
		let _userMessages = [...sessionMessages];
		const _updatedMessages = _userMessages.map(message =>
			message.messageId === updatedMessage.messageId ? { ...message, messageBody: updatedMessage.messageBody } : message
		);
		setSessionMessages(_updatedMessages);

		//update the userConversations state
		const _userConversation = [...userConversations];
		const _updatedConversations = _userConversation.map(conversation =>
			conversation.conversationId === currentConversation?.conversationId
				? { ...conversation, messages: [..._updatedMessages] }
				: conversation
		);
		setUserConverastions(_updatedConversations);
	};

	//handle insert and update conversation(add/edit);
	const _handleInsertUpdateConversation = async e => {
		e.preventDefault();
		const form = new FormData(e.target);
		const formData = Object.fromEntries(form.entries());
		const innerFormValues = innerFormRef.current.getValues();
		if (!innerFormValues.message || !formData.subject) return;
		formData.messageBody = innerFormValues.message;
		if (innerFormValues.file) {
			formData.fileSFix = innerFormValues.file.fileSFix;
			formData.fileName = innerFormValues.file.fileName;
			formData.fileData = innerFormValues.file.fileData;
		}

		const res = await Api.InsertUpdateConversation(formData);

		if (!res || (!res.conversationId && !res.isUpdate)) {
			addNotification(getTranslation(TRANSLATION_KEY, res.message), NOTIFICATION_TYPES.ERROR, 8000);

			return;
		}

		setCurrentConversation(res);
		setSessionMessages(res.messages);
		setUserConverastions(prev => [res, ...prev]);
		AddConversationUserModalVisibleToggle();
		setRedirectedFromMyCourse(false);
	};
	//handles choosing conversation onClick
	const _chooseConversation = async conversationId => {
		setReplayMessageData({});
		if (currentConversation.conversationId === conversationId) return;
		setConversationId(conversationId);
		setSessionMessages([]);
		const chosenConversation = userConversations?.find(con => con.conversationId === conversationId);
		if (!chosenConversation) return;
		const _unReadMessage = chosenConversation?.messages?.some(message => {
			if (message.messageStatusId === MESSAGE_STATUS.SENT && message.fromUserId !== user.userId) {
				return true;
			}
			return false;
		});
		if (_unReadMessage) {
			const updateConversationStatus = await Api.UpdateConversationMessageStatus(chosenConversation.conversationId);
			const conversationCopy = { ...chosenConversation };
			if (updateConversationStatus) {
				const updatedMessages = chosenConversation?.messages?.map(message => {
					if (message.messageStatusId === MESSAGE_STATUS.SENT && message.fromUserId !== user.userId) {
						message.messageStatusId = MESSAGE_STATUS.SEEN;
					}
					return message;
				});
				conversationCopy.messages = updatedMessages;
				dispatchUser({ type: USER_ACTIONS.RESET_UNREAD_MESSAGES }); //resets unreadMessages from header
			}
			setCurrentConversation(conversationCopy);
			setSessionMessages(conversationCopy.messages);
		} else {
			setCurrentConversation(chosenConversation);
			setSessionMessages(chosenConversation.messages);
		}
		if (screenSize.width <= MOBILE_SIZE) conversationRef?.current?.scrollIntoView({ behavior: 'smooth' });
	};

	const newConversationOnClick = () => {
		if (user.userApplicationStatusId !== USER_APPLICATION_STATUS.FROZEN) {
			AddConversationUserModalVisibleToggle();
			return;
		}
		addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_frozen_user_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
	};

	const handleReplayMessage = id => {
		if (!id) return;
		const _message = currentConversation?.messages?.find(mess => mess.messageId === id);
		if (!_message) return;
		if (_message.fromUserId === user.userId) {
			_message.parentMessageOwner = fromUserName;
		} else {
			_message.parentMessageOwner = toUserName;
		}
		setReplayMessageData(_message);
		innerFormRef.current.inputFocus();
	};

	//handles scroll to message onClick
	const handleScrollToMessage = parentId => {
		const parentMessageElement = conversationRef?.current?.querySelector(`[m-id="${parentId}"]`);
		if (parentMessageElement) {
			parentMessageElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
			setMarkedMessageId(parentId);
		}
		setTimeout(() => {
			setMarkedMessageId(null);
		}, 5000);
	};

	//render all conversation messages
	const renderMessages = ({
		messageId,
		messageBody,
		messageStatusId,
		fileData,
		fromUserId,
		parentMessageId,
		hasFile,
		fileSFix,
		fileName,
		dateCreated,
		index,
		ref,
	}) => {
		const currentMessageDate = new Date(dateCreated).toDateString();
		const _nextMessage = index < sessionMessages.length - 1 ? sessionMessages[index + 1] : null;
		const _nextMessageDate = new Date(_nextMessage?.dateCreated || '').toDateString();
		const showSeparator = _nextMessageDate !== currentMessageDate;
		let parentMessageToDisplay = {};
		if (parentMessageId) {
			parentMessageToDisplay = currentConversation?.messages?.find(mess => mess.messageId === parentMessageId) || {};
			if (parentMessageToDisplay.fromUserId === user.userId) {
				parentMessageToDisplay.parentMessageOwner = fromUserName;
			} else {
				parentMessageToDisplay.parentMessageOwner = toUserName;
			}
		}

		return (
			<React.Fragment key={dateCreated + index}>
				<Message
					ref={ref}
					id={messageId}
					messageBody={messageBody}
					messageStatusId={messageStatusId}
					isOwnMessage={fromUserId === user.userId}
					updateLocalMessage={updateLocalMessage}
					time={(dateCreated && new Date(dateCreated)?.toLocaleString([], { hour: '2-digit', minute: '2-digit' })) || ''}
					files={hasFile && [{ fileSFix, fileName, fileData }]}
					parentMessage={parentMessageToDisplay}
					replayMessageOnClick={handleReplayMessage}
					scrollToMessage={handleScrollToMessage}
					markedMessage={markedMessageId}
				/>
				{showSeparator && <Separator message={currentMessageDate} />}
			</React.Fragment>
		);
	};
	//render all conversation Cards
	const renderConversations = ({ conversationId, subject, dateCreated, fromUserId, fromUserName, toUserName, messages, statusId }) => {
		const _toUserName = user.userId !== fromUserId ? fromUserName : toUserName;
		// const _lastMessage = messages[0];
		// let lastMessageBody = messages[0]?.messageBody;
		// if (!lastMessageBody && _lastMessage?.fileData) {
		// 	lastMessageBody = _lastMessage?.fileName;
		// }
		const notificationIndicator = messages.some(
			messages =>
				messages.messageStatusId === MESSAGE_STATUS.SENT &&
				messages.fromUserId !== user?.userId &&
				currentConversation.conversationId !== conversationId
		);
		const unReadMessages = messages.filter(mess => mess.messageStatusId === 1).length;
		//statusId - CONVERSATION_STATUS;
		return (
			<ConversationCard
				conversationId={conversationId}
				toName={_toUserName}
				key={conversationId}
				conversationSubject={subject}
				date={dateCreated}
				handleConversationClick={_conversationId => _chooseConversation(_conversationId)}
				// lastMessage={(lastMessageBody && lastMessageBody) || ''}
				notification={notificationIndicator}
				messages={messages}
				isSelected={currentConversation.conversationId === conversationId}
				unReadMessagesCount={unReadMessages}
				Resolved={statusId === CONVERSATION_STATUS.RESOLVED}
			/>
		);
	};
	return (
		<div className="ConversationChat" ref={conversationRef}>
			<Container className="ConversationChat__container">
				{user.roleId === ROLES.Student ? (
					<Button
						small
						title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_chat_new_button`)}
						startIcon="plusIcon"
						onClick={newConversationOnClick}
					/>
				) : (
					<Button
						small
						title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_chat_back_button`)}
						startIcon="rightArrow"
						onClick={() => setConversationId()}
					/>
				)}

				<div className="ConversationChat__wrapper">
					<div className="ConversationChat__conversationCard_list">
						{isTeacher && userConversations.length > 0 && (
							<Input
								placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_chat_search_email_field`)}
								onChange={({ target }) => setFilterByEmail(target.value)}
							></Input>
						)}
						{userConversations.length > 0 && filteredUserConversations.map(conversation => renderConversations(conversation))}
					</div>
					<div className="ConversationChat__conversation_container">
						{Object.keys(currentConversation).length > 0 && (
							<div className="MessagesContainer">
								<div className="MessagesContainer__header">
									<div className="MessagesContainer__icon">
										<Icon icon="selectedAvatarIcon" size="38" />
									</div>
									<div className="MessagesContainer__header__properties">
										<div className="MessagesContainer__header__properties__title">{currentConversation.subject}</div>
										<div className="MessagesContainer__header__properties__toUser">{toUserName}</div>
									</div>
								</div>
								<div className="MessagesContainer__session" ref={sessionMessagesContainerRef}>
									{sessionMessages?.length > 0 && sessionMessages?.map((message, idx) => renderMessages({ ...message, index: idx }))}
								</div>
								<div className="MessagesContainer__session__input_container">
									{showConversationInput && (
										<ConversationInput
											onMessageSubmit={messageToSend => _handleSendMessage(messageToSend)}
											replayMessageData={replayMessageData}
											setReplayMessageData={setReplayMessageData}
											ref={innerFormRef}
										/>
									)}
									{currentConversation.statusId === CONVERSATION_STATUS.RESOLVED && (
										<div className="MessagesContainer__session__input_container__resolved">
											{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_resolved_error_message`)}
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				</div>
			</Container>
			<Modal
				extraClassName="addConversationUserModal"
				isVisible={AddConversationUserModalVisible}
				setVisibility={() => {
					AddConversationUserModalVisibleToggle();
					setRedirectedFromMyCourse(false);
				}}
			>
				<form ref={addConversationFormRef} onSubmit={_handleInsertUpdateConversation}>
					<div className="conversation__wrapper add_user_conversation">
						<div className="title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_starter_title`)}</div>
						<div className="starter">{user && `${user.fname} ${user.lname}`}</div>
						<div className="title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_recipient_title`)}</div>
						<div className="inputs_grid__starter">
							<Select
								placeHolder={teacherOptions.length !== 1 && getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_teacher_field`)}
								name="toUserId" //the teacher/user that is going to get the message
								options={teacherOptions}
							/>
						</div>
						<div className="title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_subject_title`)}</div>
						<div className="inputs_grid__properties">
							<Input
								defaultValue={''}
								name="subject"
								placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_subject_field`)}
							/>
						</div>
						<ConversationInput hideSendBtn ref={innerFormRef} preventEnterPress isCountOfCharsVisible />
						<div
							className="reminder"
							dangerouslySetInnerHTML={{ __html: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_reminder`) }}
						></div>
						<Button small title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_modal_start_button`)} />
					</div>
				</form>
			</Modal>
		</div>
	);
};

export const ConversationInput = forwardRef(
	(
		{ onMessageSubmit, inputValue, replayMessageData, setReplayMessageData, hideSendBtn, preventEnterPress, isCountOfCharsVisible = false },
		ref
	) => {
		const formRef = useRef();
		const textAreaRef = useRef();
		const fileInputRef = useRef();
		const buttonRef = useRef();
		const [fileState, setFileState] = useState();
		const getTranslation = useTranslation();

		useImperativeHandle(ref, () => ({
			getValues: () => {
				const form = new FormData(formRef.current);
				const formData = Object.fromEntries(form.entries());
				if (fileInputRef.current.value) {
					formData.file = fileState;
				}
				return formData;
			},
			inputFocus: () => {
				textAreaRef && textAreaRef.current.focus();
			},
		}));

		const clearMessageAfterSend = async e => {
			e.preventDefault();
			const form = new FormData(e.target);
			const formData = Object.fromEntries(form.entries());
			const message = formData.message;
			const dataToSend = {};
			dataToSend.parentMessageId = null;
			if (replayMessageData && replayMessageData.messageId) {
				dataToSend.parentMessageId = replayMessageData.messageId;
			}
			if (fileState) {
				dataToSend.fileData = fileState.fileData;
				dataToSend.fileName = fileState.fileName;
				dataToSend.fileSFix = fileState.fileSFix;
			}
			dataToSend.message = message;
			if (onMessageSubmit && typeof onMessageSubmit === 'function') {
				onMessageSubmit(dataToSend);
				formRef.current.reset();
				setFileState();
			}
		};

		const onEnterPress = e => {
			if (e.key === 'Enter' && e.shiftKey == false && !preventEnterPress) {
				e.preventDefault();
				formRef.current.requestSubmit();
			}
		};

		const _handleUploadFile = async e => {
			setFileState();

			const { isAllowedTypes, fileObj } = await prepereConversationFileToUpload(e);
			if (!isAllowedTypes) {
				alert(getTranslation(VALIDATIONS_TRANSLATION_KEY, `${VALIDATIONS_TRANSLATION_KEY}_file_type_error`));
				return;
			} else if (!fileObj) {
				fileInputRef.current.value = '';
				return;
			}
			setFileState(fileObj);
			textAreaRef.current.focus();
		};
		const _handleDeleteFile = e => {
			e.preventDefault();
			setFileState();
			fileInputRef.current.value = '';
			textAreaRef.current.focus();
		};

		return (
			<div className="ConversationInput">
				{fileState && <File deleteFile={_handleDeleteFile} file={fileState || undefined} />}
				{replayMessageData && Object.keys(replayMessageData).length > 0 && (
					<ParentMessage
						owner={replayMessageData.parentMessageOwner}
						message={replayMessageData.messageBody}
						messageObj={replayMessageData}
						cancelReplay={() => setReplayMessageData({})}
					/>
				)}
				<form ref={formRef} className="ConversationInputForm" onSubmit={clearMessageAfterSend}>
					<TextArea
						name="message"
						value={inputValue}
						onKeyDown={onEnterPress}
						placeholder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_input_text_field`)}
						maxlength={1500}
						isCountOfCharsVisible={isCountOfCharsVisible}
						textAreaRef={textAreaRef && textAreaRef}
					/>
					<div className="ConversationInputForm__btn_wrapper">
						<label htmlFor="file" className="fileInput">
							{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_file_field`)}
						</label>
						<input id="file" type="file" onChange={e => _handleUploadFile(e)} ref={fileInputRef} />
						{!hideSendBtn && (
							<button ref={buttonRef}>
								<Icon icon="chatSendIcon" size={26} />
							</button>
						)}
					</div>
				</form>
			</div>
		);
	}
);

export const Separator = ({ message }) => {
	const dateObj = new Date(message).toDateString();
	const today = new Date().toDateString();
	const getTranslation = useTranslation();

	return (
		<div className="Separator">
			<div className="Separator__line"></div>
			<div className="Separator__date">
				{dateObj === today ? getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_separator_date_field`) : dateObj}
			</div>
			<div className="Separator__line"></div>
		</div>
	);
};
